import React, { useRef } from 'react'
import styled from 'styled-components'
import toast from 'react-hot-toast';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    z-index: 1;
    align-items: center;
    padding: 40px 0;

    @media (max-width: 960px) {
        padding: 20px 0;
    }
`

const Wrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;
    max-width: 1350px;
    padding: 0 0 80px;
    gap: 12px;
`

const Title = styled.h2`
    font-size: 42px;
    text-align: center;
    font-weight: 700;
    margin-top: 20px;
    color: ${({ theme }) => theme.text_primary};
    letter-spacing: -0.5px;

    @media (max-width: 768px) {
        margin-top: 12px;
        font-size: 32px;
    }
`;

const Desc = styled.div`
    font-size: 17px;
    text-align: center;
    max-width: 600px;
    color: ${({ theme }) => theme.text_secondary};
    line-height: 1.6;

    @media (max-width: 768px) {
        font-size: 15px;
        padding: 0 16px;
    }
`;

const ContactForm = styled.form`
  width: 95%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.card_border};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  padding: 36px;
  border-radius: 20px;
  box-shadow: 0 8px 40px ${({ theme }) => theme.shadow};
  margin-top: 28px;
  gap: 14px;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary}33;
  }

  @media (max-width: 640px) {
    padding: 24px 20px;
  }
`

const ContactTitle = styled.div`
  font-size: 22px;
  margin-bottom: 4px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
`

const ContactInput = styled.input`
  flex: 1;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.divider};
  outline: none;
  font-size: 15px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 14px 16px;
  font-family: inherit;
  transition: all 0.25s ease;

  &::placeholder {
    color: ${({ theme }) => theme.text_tertiary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.primary_light};
  }
`

const ContactInputMessage = styled.textarea`
  flex: 1;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.divider};
  outline: none;
  font-size: 15px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 12px;
  padding: 14px 16px;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: all 0.25s ease;

  &::placeholder {
    color: ${({ theme }) => theme.text_tertiary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.primary_light};
  }
`

const ContactButton = styled.button`
  width: 100%;
  text-align: center;
  background: ${({ theme }) => theme.gradient_primary};
  padding: 14px 16px;
  margin-top: 4px;
  border-radius: 12px;
  border: none;
  color: ${({ theme }) => theme.white};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.3px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 24px ${({ theme }) => theme.primary_glow};
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: scale(0.98);
  }
`

const Contact = () => {
  const form = useRef();

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      from_email: form.current.from_email.value.trim(),
      from_name: form.current.from_name.value.trim(),
      subject: form.current.subject.value.trim(),
      message: form.current.message.value.trim(),
    };

    if (!data.from_email || !data.from_name || !data.subject || !data.message) {
      if (!data.from_email) toast.error('Please enter your email address.');
      else if (!data.from_name) toast.error('Please enter your name.');
      else if (!data.subject) toast.error('Please enter a subject.');
      else if (!data.message) toast.error('Please enter your message.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.from_email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    if (data.from_name.length < 2) {
      toast.error('Please enter a valid name (at least 2 characters).');
      return;
    }

    if (data.subject.length < 3) {
      toast.error('Please enter a subject (at least 3 characters).');
      return;
    }

    if (data.message.length < 10) {
      toast.error('Please enter a message (at least 10 characters).');
      return;
    }

    const loadingToast = toast.loading('Sending your message...');

    fetch('https://harsh-portfolio-bh2y.onrender.com/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then(async (response) => {
        toast.dismiss(loadingToast);
        if (response.ok) {
          toast.success('Email sent successfully! I\'ll get back to you soon.');
          form.current.reset();
        } else {
          const result = await response.json();
          toast.error(result.message || 'Failed to send email. Please try again.');
        }
      })
      .catch(() => {
        toast.dismiss(loadingToast);
        toast.error('Failed to send email. Please check your connection and try again.');
      });
  }

  return (
    <Container>
      <Wrapper>
        <Title>Contact</Title>
        <Desc>Feel free to reach out to me for any questions or opportunities!</Desc>
        <ContactForm ref={form} onSubmit={handleSubmit}>
          <ContactTitle>Send me a message</ContactTitle>
          <ContactInput placeholder="Your Email" name="from_email" type="email" />
          <ContactInput placeholder="Your Name" name="from_name" />
          <ContactInput placeholder="Subject" name="subject" />
          <ContactInputMessage placeholder="Your message..." rows="4" name="message" />
          <ContactButton type="submit">Send Message</ContactButton>
        </ContactForm>
      </Wrapper>
    </Container>
  )
}

export default Contact
